import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Spinner, Alert, Container, InputGroup } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useKeycloak } from '../context/KeycloakContext';
import { hasRole } from '../services/KeycloakService';
import DppAccessView from '../components/DppAccessView';
import Config from '../config/Config';
import { decodeBase64Url } from '../components/Utils';
import SectionWrapper from '../components/SectionWrapper';
import { IoSearchSharp } from 'react-icons/io5';
import '../css/AccessControlPage.css';

const AccessControlPage = () => {
    const { register, handleSubmit, setValue, formState: { errors } } = useForm();
    const [query, setQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    const intl = useIntl();
    const { isAuthenticated } = useKeycloak();
    const navigate = useNavigate();
    const location = useLocation();

    const { mainColor } = Config.appearance;
    const isAdmin = hasRole('admin');

    const placeholder = intl.formatMessage({ id: "please_enter_serial_number" });
    const errorPleaseEnterValidSerialNumber = intl.formatMessage({ id: "error_please_enter_valid_serial_number" });

    // Handle form submission
    const onSearchSubmit = (data) => {
    const searchTerm = data.searchTerm?.trim();
    if (!searchTerm) return;

    setIsLoading(true);
    setQuery(searchTerm);  // Set the query here

    // Optional: Update URL with encoded query
    const encoded = btoa(unescape(encodeURIComponent(searchTerm))); 
    const base64Url = encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    navigate(`?query=${base64Url}`, { replace: true });

    setIsLoading(false);  // End loading once the query has been set
    };

    // Prefill input + trigger search from ?query=... URL parameter
    useEffect(() => {
        if (!isAuthenticated()) return;
        if (!isAdmin) {
            navigate('/not-authorized', { replace: true });
            return;
        }

        const params = new URLSearchParams(location.search);
        const encodedQuery = params.get('query');

        if (encodedQuery) {
            try {
                const decoded = decodeBase64Url(encodedQuery);
                if (decoded && decoded.trim() && decoded.trim() !== query) {
                    setQuery(decoded.trim());
                    setValue('searchTerm', decoded.trim()); // This pre-fills the input!
                }
            } catch (err) {
                console.error('Failed to decode query parameter:', err);
            }
        }
    }, [location.search, isAuthenticated, isAdmin, navigate, setValue, query]);

    if (!isAuthenticated()) {
        return <Alert variant="danger">You are not authenticated. Please log in.</Alert>;
    }

    return (
        <Container className="access-control-container my-5">
            <SectionWrapper
                title={intl.formatMessage({ id: 'access_control' })}
                titleStyle={{ '--dpp-main-color': mainColor }}
                contentStyle={{ backgroundColor: 'transparent', padding: '15px' }}
            >
                <div className="mb-4">
                    <Form onSubmit={handleSubmit(onSearchSubmit)}>
                        {errors.searchTerm && (
                            <Alert 
                                variant="warning"
                                dismissible
                                className="mb-3">
                                {errorPleaseEnterValidSerialNumber}
                            </Alert>
                        )}

                        <InputGroup className="search-input-group" style={{ '--dpp-main-color': mainColor }}>
                            <Form.Control
                                type="search"
                                enterKeyHint="search"
                                placeholder={placeholder}
                                {...register('searchTerm', {
                                    required: intl.formatMessage({ id: 'this_field_is_required' }),
                                })}
                                className="search-input"
                            />
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isLoading}
                                className="search-button"
                            >
                                {isLoading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    <IoSearchSharp className="search-icon" />
                                )}
                            </Button>
                        </InputGroup>
                    </Form>
                </div>
                <DppAccessView query={query} />
            </SectionWrapper>
            
        </Container>
    );
};

export default AccessControlPage;